import { DialogActions, DialogContent, Typography } from '@mui/material'
import { useQueryClient } from '@tanstack/react-query'
import {
  DEFAULT_PRICE_SETTING_PRIORITY,
  PostScenarioElectricityPriceDataBody,
  SCENARIO_ELECTRICITY_PRICES_QUERY_KEY,
  ScenarioElectricityPriceData,
  useGetScenarioElectricityPrices,
  usePostScenarioElectricityPricesMutation,
} from 'api/elplan/elplan.api'
import { ELPLAN_BID_REFRESH_EVENT_QUERY_KEY } from 'api/elplan/elplan.constants'
import { ReactElement, useCallback, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import alertStore from 'store/alert/alert'
import { useAuth } from 'ui/components/AuthContext/AuthContext'
import ConfirmDialog from 'ui/components/ConfirmDialog/ConfirmDialog'
import { Dialog } from 'ui/components/Dialog/Dialog'
import InfoBanner from 'ui/components/InfoBanner/InfoBanner'
import CustomDialogTitle from 'ui/components/SettingsModal/components/CustomDialogTitle/CustomDialogTitle'
import ModalButtons from 'ui/molecules/ModalButtons/ModalButtons'
import Datetime from 'utils/datetime/datetime'

import { isSpotPriceSettledForDate } from '../spotPriceSettlement.helper'
import { ElectricityPriceScenarioModalTable } from './ElectricityPriceScenarioModalTable'
import {
  generateDefaultPriceValues,
  getScenarioPropertyMap,
  rowModelsToPriceData,
} from './electricityPriceScenario.helpers'
import { ElectricityPriceScenario, RowModel } from './types'

type ElectricityPriceScenarioModalProps = {
  closeModal: () => void
  scenarios: ElectricityPriceScenario[]
  chosenDate: {
    startTime: ISODateTime
    endTime: ISODateTime
  }
}

export default function ElectricityPriceScenarioModal({
  closeModal,
  scenarios,
  chosenDate,
}: ElectricityPriceScenarioModalProps): ReactElement {
  const { t } = useTranslation()
  const { activeSystem } = useAuth()
  const [closeConfirmOpen, setCloseConfirmOpen] = useState(false)
  const [noChangesInModal, setNoChangesInModal] = useState(true)
  const [submitDisabledText, setSubmitDisabledText] = useState(t('No new values, change a value to be able to save'))
  const [scenarioPriceValues, setScenarioPriceValues] = useState<ScenarioElectricityPriceData[]>([])
  const dialogContentRef = useRef<HTMLDivElement>(null)
  const queryClient = useQueryClient()

  const scenariosWithoutOverviews = useMemo(() => scenarios.slice(1), [scenarios])
  const scenarioPropertyMap = getScenarioPropertyMap(scenariosWithoutOverviews)
  const spotPriceSettled = isSpotPriceSettledForDate(chosenDate.startTime, Datetime.getISONow(0, false))
  const spotPriceSettledMessage = t(
    'The spot price for this date has already been settled, choose a later date to save'
  )
  const postScenarioPrices = usePostScenarioElectricityPricesMutation(
    activeSystem?.primary_digital_twin?.uid,
    activeSystem?.id,
    chosenDate.startTime,
    chosenDate.endTime,
    scenarioPropertyMap
  )

  const { data: electricityPriceData } = useGetScenarioElectricityPrices(
    scenarioPropertyMap,
    chosenDate.startTime,
    chosenDate.endTime,
    scenarios
      .map((scenario) => scenario.setting_priority)
      .filter((priority): priority is number => priority !== undefined)
  )

  const onSubmit = useCallback(async () => {
    if (!scenarioPriceValues.length) {
      return
    }

    const body: PostScenarioElectricityPriceDataBody = {
      start_time: chosenDate.startTime,
      end_time: chosenDate.endTime,
      settings: scenarioPriceValues.map((value) => {
        const matchingScenario = scenarios.find((scenario) => scenario.id === value.id)
        return {
          level: 'model',
          name: activeSystem?.primary_digital_twin?.name || '',
          attribute: matchingScenario?.uiconfig_override_alias.electricity_price_override_prop?.toString() || '',
          value: value.values.map((v) => v.value ?? null),
          comment: matchingScenario?.scenario_name || '',
          priority: matchingScenario?.setting_priority || DEFAULT_PRICE_SETTING_PRIORITY,
        }
      }),
    }

    setSubmitDisabledText(t('Saving electricity prices'))
    setNoChangesInModal(true)

    try {
      await postScenarioPrices.mutateAsync(body)

      queryClient.invalidateQueries({
        queryKey: [SCENARIO_ELECTRICITY_PRICES_QUERY_KEY],
      })

      queryClient.invalidateQueries({
        queryKey: [ELPLAN_BID_REFRESH_EVENT_QUERY_KEY],
      })

      closeModal()
      alertStore.success(t('Successfully updated scenario prices'))
    } catch (_error) {
      alertStore.error(t('Failed to update scenario prices'))
      setSubmitDisabledText(t('No new values, change a value to be able to save'))
      setNoChangesInModal(false)
    }
  }, [
    activeSystem?.primary_digital_twin?.name,
    chosenDate.endTime,
    chosenDate.startTime,
    closeModal,
    postScenarioPrices,
    scenarioPriceValues,
    scenarios,
    t,
    queryClient,
  ])

  return (
    <>
      <Dialog
        open={true}
        maxWidth="lg"
        onClose={(_, reason) => {
          if (reason === 'escapeKeyDown') {
            closeModal()
          }
        }}
      >
        <CustomDialogTitle
          paddingBottom="10px" //Less padding to remove vertical scrollbar when horizontal scrollbar is showing
          title={t('Add electricity price for scenarios')}
          handleClose={closeModal}
        />
        <DialogContent
          ref={dialogContentRef}
          sx={{
            paddingTop: 1,
            paddingRight: 3,
            paddingBottom: 0, //Remove bottom padding to remove vertical scrollbar on bigger screens
            paddingLeft: 3,
          }}
        >
          <Typography variant="body2" sx={{ mb: 2 }}>
            <strong>{t('Date')}:</strong> {Datetime.toLocalTime(chosenDate.startTime, 'shortDayText')}{' '}
            {Datetime.toLocalTime(chosenDate.startTime, 'onlyDate')}
          </Typography>
          <ElectricityPriceScenarioModalTable
            scenarios={scenariosWithoutOverviews}
            chosenDate={chosenDate}
            electricityPriceData={electricityPriceData}
            onChange={(rowModels: RowModel[]) => {
              const newPriceValues = rowModelsToPriceData(rowModels)
              const oldPriceValues =
                electricityPriceData?.length === 0
                  ? generateDefaultPriceValues(scenariosWithoutOverviews, chosenDate.startTime, chosenDate.endTime)
                  : electricityPriceData

              const priceValuesAreSame = JSON.stringify(oldPriceValues) === JSON.stringify(newPriceValues)
              setNoChangesInModal(priceValuesAreSame)
              setScenarioPriceValues(newPriceValues)
            }}
          />
        </DialogContent>
        <DialogActions>
          <div className="flex w-full flex-col items-end gap-2">
            {spotPriceSettled && (
              <div className="w-fit max-w-full">
                <InfoBanner text={spotPriceSettledMessage} style="info" />
              </div>
            )}
            <ModalButtons
              disableSubmit={noChangesInModal || spotPriceSettled}
              submitDisabledText={spotPriceSettled ? spotPriceSettledMessage : submitDisabledText}
              onSubmit={onSubmit}
              onCancel={() => {
                if (!noChangesInModal) {
                  setCloseConfirmOpen(true)
                } else {
                  closeModal()
                }
              }}
            />
          </div>
        </DialogActions>
      </Dialog>
      <ConfirmDialog
        open={closeConfirmOpen}
        onClose={() => {
          setCloseConfirmOpen(false)
        }}
        onConfirm={() => {
          setCloseConfirmOpen(false)
          closeModal()
        }}
        textObject={{
          title: t('Do you really want to close without saving?'),
          text: t(
            'Currently you have unsaved electricity prices, are you completely sure you would like to exit without saving?'
          ),
          confirm: t('Discard changes'),
          deny: t('Go back to editing'),
        }}
      />
    </>
  )
}
